import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Grid, Paper, Box, Typography, Button, IconButton, Divider,
  TextField, CircularProgress, Table, TableBody, TableHead, TableRow, TableCell, TableContainer, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemIcon, ListItemText,
  TableSortLabel, Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CloudIcon from '@mui/icons-material/Cloud';
import PeopleIcon from '@mui/icons-material/People';
import ConsumptionIcon from '@mui/icons-material/TrendingUp';
import AlertIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SpeedIcon from '@mui/icons-material/Speed';
import CloseIcon from '@mui/icons-material/Close';
import CloudConnectDialog from '../components/CloudConnectDialog';
import StatCard from '../components/StatCard';
import { listClouds, getCloud, refreshCloud, deleteCloud, notifyCloud } from '../api/clouds';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, Legend } from 'recharts';

const PRIMARY = '#054693';

function Loader() { return <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>; }

export default function CloudMonitorPage({ provider }) {
  const [clouds, setClouds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cloudDetail, setCloudDetail] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchSku, setSearchSku] = useState('');
  const [alertsDialogOpen, setAlertsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cloudToDelete, setCloudToDelete] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'sku', direction: 'asc' });

  useEffect(() => { void loadList(); }, [provider]);

  async function loadList() {
    setLoading(true);
    try {
      const res = await listClouds();
      const cloudList = Array.isArray(res) ? res : (res?.data ?? []);
      setClouds(cloudList);
      // Find cloud for this provider
      const selectedCloud = cloudList.find(c => c.provider === provider);
      if (selectedCloud) {
        await loadDetail(selectedCloud.id);
      } else {
        setCloudDetail(null);
      }
    } catch (err) {
      console.error(err);
      setClouds([]);
      setCloudDetail(null);
    } finally { 
      setLoading(false); 
    }
  }

  async function loadDetail(id) {
    setActionLoading(true);
    try {
      const detail = await getCloud(id);
      
      if (detail?.provider === 'azure' && detail?.summary) {
      }
      
      setCloudDetail(detail);
    } catch (err) {
      console.error('❌ [CloudMonitor] Error loading detail:', err);
      setCloudDetail(null);
    } finally { 
      setActionLoading(false); 
    }
  }

  async function handleSync(id) {
    setActionLoading(true);
    try {
      const result = await refreshCloud(id);
      await loadDetail(id);
      await loadList();
    } catch (err) { 
      console.error('❌ [CloudMonitor] Sync error:', err);
      console.error('❌ [CloudMonitor] Error response:', err?.response?.data);
    }
    setActionLoading(false);
  }

  async function handleDelete(id) {
    const cloud = clouds.find(c => c.id === id);
    setCloudToDelete(cloud);
    setDeleteDialogOpen(true);
  }

  async function confirmDelete() {
    if (!cloudToDelete) return;
    
    setActionLoading(true);
    try {
      await deleteCloud(cloudToDelete.id);
      setCloudDetail(null);
      setDeleteDialogOpen(false);
      setCloudToDelete(null);
      await loadList();
    } catch (err) { 
      console.error('Delete cloud error:', err);
      alert(`Failed to delete connection: ${err?.response?.data?.error || err?.response?.data?.detail || err.message || 'Unknown error'}`);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleNotify(id) {
    setActionLoading(true);
    try {
      // Use default recipients from env on server side
      await notifyCloud(id, {});
      alert('Notification triggered (if configured).');
    } catch (err) {
      console.error(err);
      alert('Notify failed: ' + (err?.response?.data?.error || err.message));
    } finally { setActionLoading(false); }
  }

  const summary = cloudDetail?.summary || { utilization: [], unlicensedUsers: [], alerts: [], fetchedAt: null };
  
  // Debug logging to see what data is coming from backend
  
  if (cloudDetail?.provider === 'azure') {
    if (summary.subscriptions && summary.subscriptions.length > 0) {
      summary.subscriptions.forEach((sub, idx) => {
      });
    } else {
      console.warn('  ⚠️ NO SUBSCRIPTIONS FOUND IN SUMMARY!');
    }
  } else {
  }
  
  // Function to determine if a license is free/irrelevant
  const isFreeOrIrrelevantLicense = (sku, total) => {
    const skuName = (sku || '').toLowerCase();
    const totalCount = Number(total || 0);

    if (totalCount >= 10000) return true;
    
    const freeSkuPatterns = [
      'free', 'trial', 'developer', 'student', 'nonprofit', 'charity',
      'rights management', 'intune for education', 'microsoft teams exploratory',
      'power automate per user with attended rpa', 'power bi (free)',
      'azure active directory free', 'azure ad free', 'microsoft 365 apps for students',
      'office 365 a1', 'microsoft defender for cloud apps discovery',
      'microsoft defender for endpoint p1', 'microsoft cloud app security discovery',
      'azure information protection premium p1', 'azure rights management services',
      'microsoft stream', 'microsoft forms', 'microsoft bookings', 'microsoft staffhub',
      'yammer', 'microsoft planner', 'microsoft to-do', 'microsoft whiteboard',
      'microsoft search', 'microsoft delve', 'microsoft sway',
      'power platform trial', 'dynamics 365 trial', 'project online essentials'
    ];
    
    return freeSkuPatterns.some(pattern => skuName.includes(pattern));
  };
  
  const { totals, advancedMetrics, enhancedAlerts } = useMemo(() => {
    let totalLic = 0, totalCons = 0, totalWasted = 0;
    let criticalLicenses = 0, underutilizedLicenses = 0, overAllocated = 0;
    const newAlerts = [];

    (summary.utilization || []).forEach(u => {
      const total = Number(u.total || 0);
      const consumed = Number(u.consumed || 0);
      
      // Skip free or irrelevant licenses (including 10k+ licenses)
      if (isFreeOrIrrelevantLicense(u.sku || u.skuLabel, total)) {
        return;
      }

      const unused = Math.max(0, total - consumed);
      const utilization = total > 0 ? (consumed / total) * 100 : 0;
      
      totalLic += total;
      totalCons += consumed;
      totalWasted += unused;
      
      // Generate enhanced alerts based on usage patterns
      if (utilization >= 95) {
        criticalLicenses++;
        newAlerts.push({
          type: 'critical',
          title: 'Critical License Shortage',
          message: `${u.sku || u.skuLabel} is ${utilization.toFixed(1)}% utilized (${consumed}/${total}). Risk of running out soon!`,
          severity: 'error',
          action: 'Consider purchasing additional licenses'
        });
      } else if (utilization >= 80) {
        newAlerts.push({
          type: 'warning',
          title: 'High License Usage',
          message: `${u.sku || u.skuLabel} is ${utilization.toFixed(1)}% utilized (${consumed}/${total}). Monitor closely for capacity planning.`,
          severity: 'warning',
          action: 'Plan for potential license expansion'
        });
      } else if (utilization < 30 && total >= 5) {
        underutilizedLicenses++;
        newAlerts.push({
          type: 'underutilized',
          title: 'License Underutilization',
          message: `${u.sku || u.skuLabel} has low utilization at ${utilization.toFixed(1)}% (${consumed}/${total}). ${unused} licenses unused.`,
          severity: 'info',
          action: `Consider reducing by ${Math.floor(unused * 0.6)} licenses`
        });
      }

      // Check for over-allocation patterns
      if (total > consumed * 3 && total >= 10) {
        overAllocated++;
        newAlerts.push({
          type: 'overallocation',
          title: 'Potential Over-Allocation',
          message: `${u.sku || u.skuLabel} has ${total} licenses but only ${consumed} are used (${utilization.toFixed(1)}% utilization). Significant over-provisioning detected.`,
          severity: 'warning',
          action: 'Review actual usage needs and consider reducing allocation'
        });
      }

      // Check for completely unused licenses
      if (consumed === 0 && total > 0) {
        newAlerts.push({
          type: 'unused',
          title: 'Completely Unused License',
          message: `${u.sku || u.skuLabel} has ${total} licenses with zero usage. This subscription may not be needed.`,
          severity: 'error',
          action: 'Consider canceling this subscription'
        });
      }
    });

    // Add unlicensed user alerts
    if (summary.unlicensedUsers?.length > 0) {
      newAlerts.push({
        type: 'unlicensed',
        title: 'Unlicensed Users Detected',
        message: `${summary.unlicensedUsers.length} users are accessing services without proper licenses. This could pose compliance and security risks.`,
        severity: 'error',
        action: 'Assign licenses immediately or review access permissions'
      });
    }

    // Add efficiency recommendations
    const wastePercentage = totalLic > 0 ? (totalWasted / totalLic) * 100 : 0;
    const utilizationRate = totalLic > 0 ? (totalCons / totalLic) * 100 : 0;

    if (wastePercentage > 40) {
      newAlerts.push({
        type: 'efficiency',
        title: 'Major License Optimization Opportunity',
        message: `${wastePercentage.toFixed(1)}% of licenses are unused (${totalWasted} out of ${totalLic}). Significant optimization possible.`,
        severity: 'warning',
        action: 'Conduct comprehensive license review and optimization'
      });
    } else if (wastePercentage > 25) {
      newAlerts.push({
        type: 'efficiency',
        title: 'License Optimization Opportunity',
        message: `${wastePercentage.toFixed(1)}% of licenses are unused (${totalWasted} out of ${totalLic}). Consider optimization.`,
        severity: 'info',
        action: 'Review and optimize license allocation'
      });
    }

    // Check for usage patterns across all SKUs
    const activeSkus = (summary.utilization || []).filter(u => {
      const total = Number(u.total || 0);
      return !isFreeOrIrrelevantLicense(u.sku || u.skuLabel, total) && Number(u.consumed || 0) > 0;
    }).length;
    
    const unusedSkus = (summary.utilization || []).filter(u => {
      const total = Number(u.total || 0);
      return !isFreeOrIrrelevantLicense(u.sku || u.skuLabel, total) && 
             Number(u.consumed || 0) === 0 && total > 0;
    });

    if (unusedSkus.length > 0) {
      newAlerts.push({
        type: 'unused',
        title: 'Completely Unused License Types',
        message: `${unusedSkus.length} license types have zero usage: ${unusedSkus.map(u => u.sku || u.skuLabel).slice(0, 3).join(', ')}${unusedSkus.length > 3 ? '...' : ''}`,
        severity: 'warning',
        action: 'Consider canceling these unused subscriptions'
      });
    }

    // Add alerts for very high utilization across multiple SKUs
    const highUtilizationSkus = (summary.utilization || []).filter(u => {
      const total = Number(u.total || 0);
      const consumed = Number(u.consumed || 0);
      const utilization = total > 0 ? (consumed / total) * 100 : 0;
      return !isFreeOrIrrelevantLicense(u.sku || u.skuLabel, total) && utilization >= 90;
    });

    if (highUtilizationSkus.length >= 3) {
      newAlerts.push({
        type: 'capacity',
        title: 'Multiple High-Utilization SKUs',
        message: `${highUtilizationSkus.length} license types are at 90%+ utilization. This indicates potential capacity constraints.`,
        severity: 'warning',
        action: 'Plan for license expansion across multiple services'
      });
    }

    // Add general health alert if no specific issues found
    if (newAlerts.length === 0 && totalLic > 0) {
      newAlerts.push({
        type: 'health',
        title: 'License Health Status',
        message: `License utilization is within normal ranges. Overall efficiency: ${utilizationRate.toFixed(1)}%`,
        severity: 'success',
        action: 'Continue monitoring usage patterns'
      });
    }

    return {
      totals: { 
        totalLic, 
        totalCons, 
        unassigned: totalWasted, 
        skus: (summary.utilization || []).filter(u => {
          const total = Number(u.total || 0);
          return !isFreeOrIrrelevantLicense(u.sku || u.skuLabel, total);
        }).length,
        activeSkus,
        unusedSkus: unusedSkus.length
      },
      advancedMetrics: {
        utilizationRate,
        wastePercentage,
        efficiency: utilizationRate > 75 ? 'High' : utilizationRate > 50 ? 'Medium' : 'Low',
        criticalLicenses,
        underutilizedLicenses,
        overAllocated,
        totalUsers: summary.usersCount || summary.totalUsers || (summary.unlicensedUsers?.length || 0) + totalCons || 0,
        licensedUsers: totalCons,
        unlicensedUsers: summary.unlicensedUsers?.length || 0
      },
      enhancedAlerts: [...(summary.alerts || []).filter(alert => alert && alert.title && alert.message), ...newAlerts]
    };
  }, [summary]);

  const licenseRows = useMemo(() => {
    // Filter out free/irrelevant licenses first (including 10k+ licenses)
    let filtered = (summary.utilization || [])
      .filter(r => {
        const total = Number(r.total || 0);
        return !isFreeOrIrrelevantLicense(r.sku || r.skuLabel, total);
      })
      .filter(r => {
        if (!searchSku) return true;
        return (r.sku || r.skuLabel || '').toLowerCase().includes(searchSku.toLowerCase());
      });

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'sku':
          aValue = (a.sku || a.skuLabel || '').toLowerCase();
          bValue = (b.sku || b.skuLabel || '').toLowerCase();
          break;
        case 'total':
          aValue = Number(a.total || 0);
          bValue = Number(b.total || 0);
          break;
        case 'consumed':
          aValue = Number(a.consumed || 0);
          bValue = Number(b.consumed || 0);
          break;
        case 'available':
          aValue = Math.max(0, Number(a.total || 0) - Number(a.consumed || 0));
          bValue = Math.max(0, Number(b.total || 0) - Number(b.consumed || 0));
          break;
        case 'usage':
          aValue = a.total ? (a.consumed / a.total) * 100 : 0;
          bValue = b.total ? (b.consumed / b.total) * 100 : 0;
          break;
        default:
          return 0;
      }

      if (sortConfig.direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [summary, searchSku, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
      {loading ? (
        <Loader />
      ) : !clouds.some(c => c.provider === provider) ? (
        <Paper sx={{ p: { xs: 3, sm: 4, md: 5 }, maxWidth: 600, mx: 'auto', mt: { xs: 2, sm: 4, md: 6 }, borderRadius: 3, boxShadow: 3 }}>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
            <Box sx={{ mb: 2 }}>
              <img 
                src={provider === 'm365' ? '/m365.jpg' : provider === 'azure' ? '/azure.jpg' : '/aws.png'} 
                alt={provider} 
                style={{ width: 56, height: 56, borderRadius: 8, boxShadow: '0 2px 8px #e0e7ef' }} 
              />
            </Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {provider === 'm365' ? 'Connect Microsoft 365' : provider === 'azure' ? 'Connect Microsoft Azure' : 'Connect Amazon Web Services'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
              To start monitoring your {provider === 'm365' ? 'M365' : provider === 'azure' ? 'Azure' : 'AWS'} cloud, please add your account below.
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={() => setDlgOpen(true)}
              sx={{ bgcolor: PRIMARY, minWidth: 180, fontWeight: 600, fontSize: '1rem' }}
            >
              Add {provider === 'm365' ? 'M365' : provider === 'azure' ? 'Azure' : 'AWS'} Cloud
            </Button>
            <Divider sx={{ my: 3, width: '100%' }} />
            <Box sx={{ width: '100%', bgcolor: '#f8fafc', p: 3, borderRadius: 2, boxShadow: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                How to get your credentials:
              </Typography>
              {provider === 'aws' ? (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    1. Go to <b>AWS Console &gt; IAM &gt; Users</b> and create a new user or select existing.<br />
                    2. Create or view <b>Access keys</b> for programmatic access.<br />
                    3. Copy the <b>Access Key ID</b> and <b>Secret Access Key</b>.<br />
                    4. Ensure the user has permissions for EC2, S3, RDS, Lambda, Cost Explorer (ReadOnlyAccess policy recommended).
                  </Typography>
                </>
              ) : provider === 'azure' ? (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    1. Go to <b>Azure Portal &gt; App registrations</b> and register a new application.<br />
                    2. Copy the <b>Application (client) ID</b> and <b>Directory (tenant) ID</b>.<br />
                    3. Under <b>Certificates &amp; secrets</b>, create a new client secret and copy its value.<br />
                    4. Assign required API permissions (Azure Resource Manager, etc.).
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    1. Go to <b>Azure Portal &gt; App registrations</b> and register a new application for Microsoft Graph.<br />
                    2. Copy the <b>Application (client) ID</b> and <b>Directory (tenant) ID</b>.<br />
                    3. Under <b>Certificates &amp; secrets</b>, create a new client secret and copy its value.<br />
                    4. Assign required API permissions (Microsoft Graph, etc.).
                  </Typography>
                </>
              )}
              <Typography variant="caption" color="text.secondary">
                Need help? See our documentation or contact support.
              </Typography>
            </Box>
          </Box>
          <CloudConnectDialog 
            open={dlgOpen} 
            defaultProvider={provider}
            onClose={() => setDlgOpen(false)}
            onSuccess={async () => { 
              setDlgOpen(false); 
              await loadList(); 
            }} 
          />
        </Paper>
      ) : !cloudDetail ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>Loading cloud data...</Typography>
        </Paper>
      ) : (
        <Box>
          {/* Cloud Platform Header */}
          <Paper sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap={{ xs: 'wrap', sm: 'nowrap' }} gap={2}>
              <Box display="flex" alignItems="center" gap={2} flex={1}>
                {/* Cloud Platform Logo */}
                <Box
                  component="img"
                  src={
                    cloudDetail.provider?.toLowerCase() === 'm365' ? '/m365.jpg' : 
                    cloudDetail.provider?.toLowerCase() === 'aws' ? '/aws.png' : 
                    '/azure.jpg'
                  }
                  alt={cloudDetail.provider?.toUpperCase()}
                  sx={{
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    borderRadius: 2,
                    objectFit: 'cover',
                    border: '2px solid white',
                    boxShadow: 2
                  }}
                />
                
                <Box flex={1}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: PRIMARY, mb: 0.5, fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' } }}>
                    {cloudDetail.provider?.toUpperCase() === 'M365' ? 'Microsoft 365' : 
                     cloudDetail.provider?.toUpperCase() === 'AWS' ? 'Amazon Web Services' : 
                     'Microsoft Azure'} Dashboard
                  </Typography>
                  
                  <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} flexWrap="wrap">
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Last sync: {cloudDetail.lastCheckAt 
                        ? new Date(cloudDetail.lastCheckAt).toLocaleString() 
                        : (summary.fetchedAt ? new Date(summary.fetchedAt).toLocaleString() : '—')
                      }
                    </Typography>
                    
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, display: { xs: 'none', sm: 'block' } }}>
                      {cloudDetail.provider === 'aws' ? `Region: ${cloudDetail.awsRegion || 'us-east-1'}` : `Tenant: ${cloudDetail.tenantId}`}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              
              {/* Action Buttons */}
              <Box display="flex" gap={1}>
                <Tooltip title="Refresh cloud data">
                  <IconButton 
                    onClick={() => handleSync(cloudDetail.id)} 
                    disabled={actionLoading}
                    sx={{ 
                      bgcolor: 'white', 
                      boxShadow: 1,
                      '&:hover': { 
                        bgcolor: 'grey.50',
                        transform: 'scale(1.05)' 
                      },
                      transition: 'all 0.2s'
                    }}
                  >
                    <RefreshIcon sx={{ color: PRIMARY }} />
                  </IconButton>
                </Tooltip>
                
                <Tooltip title="Remove cloud connection">
                  <IconButton 
                    onClick={() => handleDelete(cloudDetail.id)} 
                    disabled={actionLoading}
                    sx={{ 
                      bgcolor: 'white', 
                      boxShadow: 1,
                      '&:hover': { 
                        bgcolor: '#fee',
                        transform: 'scale(1.05)' 
                      },
                      transition: 'all 0.2s'
                    }}
                  >
                    <DeleteIcon sx={{ color: '#d32f2f' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Paper>

          {/* Statistics Cards */}
          <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }}>
            {cloudDetail.provider === 'aws' ? (
              // AWS-specific stats
              <>
                <Grid item xs={6} sm={6} md={2.4}>
                  <StatCard
                    label="EC2 Instances"
                    value={summary.ec2?.total || 0}
                    caption={`${summary.ec2?.running || 0} running • ${summary.ec2?.stopped || 0} stopped`}
                    icon={<CloudIcon sx={{ color: PRIMARY }} />}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={2.4}>
                  <StatCard
                    label="S3 Buckets"
                    value={summary.s3?.total || 0}
                    caption={`${summary.s3?.totalSizeGB || 0} GB total`}
                    icon={<ConsumptionIcon sx={{ color: PRIMARY }} />}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={2.4}>
                  <StatCard
                    label="RDS Databases"
                    value={summary.rds?.total || 0}
                    caption="Database instances"
                    icon={<CloudIcon sx={{ color: PRIMARY }} />}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={2.4}>
                  <StatCard
                    label="Lambda Functions"
                    value={summary.lambda?.total || 0}
                    caption="Serverless functions"
                    icon={<SpeedIcon sx={{ color: PRIMARY }} />}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={2.4}>
                  <Box 
                    onClick={() => setAlertsDialogOpen(true)}
                    sx={{ cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)' }, transition: 'transform 0.2s' }}
                  >
                    <StatCard
                      label="Alerts"
                      value={summary.alerts?.length || 0}
                      caption="Click to view details"
                      icon={<AlertIcon sx={{ color: (summary.alerts?.length || 0) > 0 ? 'error.main' : 'success.main' }} />}
                    />
                  </Box>
                </Grid>
              </>
            ) : (
              // M365/Azure license-based stats
              <>
                <Grid item xs={6} sm={6} md={2.4}>
                  <StatCard
                    label="Licensed Users"
                    value={advancedMetrics.licensedUsers?.toLocaleString() || '—'}
                    caption={`${advancedMetrics.unlicensedUsers} unlicensed • ${totals.totalLic} total licenses`}
                    icon={<PeopleIcon sx={{ color: PRIMARY }} />}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={2.4}>
                  <StatCard
                    label="License Efficiency"
                    value={advancedMetrics.efficiency}
                    caption={`${advancedMetrics.utilizationRate.toFixed(1)}% utilization`}
                    icon={<SpeedIcon sx={{ color: advancedMetrics.efficiency === 'High' ? 'success.main' : advancedMetrics.efficiency === 'Medium' ? 'warning.main' : 'error.main' }} />}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={2.4}>
                  <StatCard
                    label="License Waste"
                    value={`${advancedMetrics.wastePercentage.toFixed(1)}%`}
                    caption={`${totals.unassigned} unused licenses`}
                    icon={<TrendingDownIcon sx={{ color: advancedMetrics.wastePercentage > 30 ? 'error.main' : advancedMetrics.wastePercentage > 15 ? 'warning.main' : 'success.main' }} />}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={2.4}>
                  <StatCard
                    label="Active SKUs"
                    value={totals.activeSkus}
                    caption={`${totals.unusedSkus} completely unused`}
                    icon={<CloudIcon sx={{ color: PRIMARY }} />}
                  />
                </Grid>
                <Grid item xs={6} sm={6} md={2.4}>
                  <Box 
                    onClick={() => setAlertsDialogOpen(true)}
                    sx={{ cursor: 'pointer', '&:hover': { transform: 'translateY(-2px)' }, transition: 'transform 0.2s' }}
                  >
                    <StatCard
                      label="Alerts"
                      value={enhancedAlerts.length}
                      caption={advancedMetrics.criticalLicenses > 0 ? `${advancedMetrics.criticalLicenses} critical` : 'Click to view details'}
                      icon={<AlertIcon sx={{ color: enhancedAlerts.length > 0 ? 'error.main' : 'success.main' }} />}
                    />
                  </Box>
                </Grid>
              </>
            )}
          </Grid>

          {/* License Usage Table - Full Width OR AWS Resources Table */}
          {cloudDetail.provider === 'aws' ? (
            // AWS Resources Tables
            <Box>
              {/* AWS Analytics Summary */}
              {summary.analytics && (
                <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                    💡 AWS Cost & Usage Analytics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                          ${summary.analytics.projectedMonthlyCost || '0.00'}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Projected Monthly Cost
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700 }}>
                          {summary.analytics.totalResources || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total Resources
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: summary.analytics.costOptimizationOpportunities > 0 ? '#ffd700' : 'inherit' }}>
                          {summary.analytics.costOptimizationOpportunities || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Cost Savings Opportunities
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: summary.analytics.securityIssues > 0 ? '#ff6b6b' : 'inherit' }}>
                          {summary.analytics.securityIssues || 0}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Security Issues
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  {summary.analytics.topSpendingServices && summary.analytics.topSpendingServices.length > 0 && (
                    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Top Spending Services:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {summary.analytics.topSpendingServices.slice(0, 5).map((svc, idx) => (
                          <Chip 
                            key={idx}
                            label={`${svc.service}: $${svc.cost}`}
                            sx={{ 
                              bgcolor: 'rgba(255,255,255,0.2)', 
                              color: 'white',
                              fontWeight: 600,
                              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Paper>
              )}

              {/* EC2 Instances Table */}
              {summary.ec2?.instances && summary.ec2.instances.length > 0 && (
                <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' }, mb: 2 }}>
                    EC2 Instances ({summary.ec2.total})
                  </Typography>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Instance ID</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>State</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Region</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Public IP</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summary.ec2.instances.map((instance, idx) => (
                          <TableRow key={instance.instanceId || idx} hover>
                            <TableCell>{instance.name}</TableCell>
                            <TableCell>{instance.instanceId}</TableCell>
                            <TableCell>{instance.instanceType}</TableCell>
                            <TableCell>
                              <Chip 
                                label={instance.state} 
                                size="small" 
                                color={instance.state === 'running' ? 'success' : instance.state === 'stopped' ? 'warning' : 'default'}
                              />
                            </TableCell>
                            <TableCell>{instance.region}</TableCell>
                            <TableCell>{instance.publicIp}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* S3 Buckets Table */}
              {summary.s3?.buckets && summary.s3.buckets.length > 0 && (
                <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' }, mb: 2 }}>
                    S3 Buckets ({summary.s3.total})
                  </Typography>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Bucket Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Region</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Objects</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Size (GB)</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summary.s3.buckets.map((bucket, idx) => (
                          <TableRow key={bucket.name || idx} hover>
                            <TableCell>{bucket.name}</TableCell>
                            <TableCell>{bucket.region}</TableCell>
                            <TableCell align="right">{bucket.objectCount?.toLocaleString()}</TableCell>
                            <TableCell align="right">{bucket.sizeGB}</TableCell>
                            <TableCell>{bucket.creationDate ? new Date(bucket.creationDate).toLocaleDateString() : 'N/A'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* RDS Instances Table */}
              {summary.rds?.instances && summary.rds.instances.length > 0 && (
                <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' }, mb: 2 }}>
                    RDS Database Instances ({summary.rds.total})
                  </Typography>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Identifier</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Engine</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Instance Class</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Region</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summary.rds.instances.map((db, idx) => (
                          <TableRow key={db.identifier || idx} hover>
                            <TableCell>{db.identifier}</TableCell>
                            <TableCell>{db.engine} {db.engineVersion}</TableCell>
                            <TableCell>{db.instanceClass}</TableCell>
                            <TableCell>
                              <Chip 
                                label={db.status} 
                                size="small" 
                                color={db.status === 'available' ? 'success' : 'warning'}
                              />
                            </TableCell>
                            <TableCell>{db.region}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {/* Lambda Functions Table */}
              {summary.lambda?.functions && summary.lambda.functions.length > 0 && (
                <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' }, mb: 2 }}>
                    Lambda Functions ({summary.lambda.total})
                  </Typography>
                  <TableContainer sx={{ maxHeight: 400 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Function Name</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Runtime</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Memory (MB)</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Timeout (s)</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Region</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {summary.lambda.functions.map((func, idx) => (
                          <TableRow key={func.functionName || idx} hover>
                            <TableCell>{func.functionName}</TableCell>
                            <TableCell>{func.runtime}</TableCell>
                            <TableCell align="right">{func.memorySize}</TableCell>
                            <TableCell align="right">{func.timeout}</TableCell>
                            <TableCell>{func.region}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
            </Box>
          ) : (
          <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                License Usage Details
              </Typography>
              <TextField 
                size="small" 
                placeholder="Search SKU..." 
                value={searchSku} 
                onChange={(e) => setSearchSku(e.target.value)}
                sx={{ minWidth: { xs: '100%', sm: 200 } }}
              />
            </Box>

            <TableContainer sx={{ height: { xs: 400, md: 'calc(100vh - 420px)' }, minHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortConfig.key === 'sku'}
                        direction={sortConfig.key === 'sku' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('sku')}
                      >
                        SKU / License Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, display: { xs: 'none', sm: 'table-cell' } }}>
                      <TableSortLabel
                        active={sortConfig.key === 'total'}
                        direction={sortConfig.key === 'total' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('total')}
                      >
                        Total
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortConfig.key === 'consumed'}
                        direction={sortConfig.key === 'consumed' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('consumed')}
                      >
                        Consumed
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>
                      <TableSortLabel
                        active={sortConfig.key === 'available'}
                        direction={sortConfig.key === 'available' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('available')}
                      >
                        Available
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortConfig.key === 'usage'}
                        direction={sortConfig.key === 'usage' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSort('usage')}
                      >
                        Usage %
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, display: { xs: 'none', md: 'table-cell' } }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, display: { xs: 'none', sm: 'table-cell' } }}>Risk Level</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {licenseRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {searchSku ? 'No licenses match your search criteria' : 'No license data available'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    licenseRows.map((r, i) => {
                      const total = Number(r.total || 0);
                      const consumed = Number(r.consumed || 0);
                      const available = Math.max(0, total - consumed);
                      const pct = total > 0 ? Math.round((consumed / total) * 100) : 0;
                      
                      // Determine risk level based on usage patterns
                      const getRiskLevel = () => {
                        if (pct >= 95) return { level: 'Critical', color: 'error', reason: 'May run out soon' };
                        if (pct >= 85) return { level: 'High', color: 'warning', reason: 'Nearing capacity' };
                        if (pct < 25 && total > 3) return { level: 'Overprovisioned', color: 'info', reason: 'Too many licenses' };
                        if (pct === 0 && total > 0) return { level: 'Unused', color: 'error', reason: 'Zero usage' };
                        return { level: 'Normal', color: 'success', reason: 'Optimal range' };
                      };
                      
                      const risk = getRiskLevel();
                      
                      return (
                        <TableRow key={(r.sku || r.skuLabel) + i} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                {r.sku || r.skuLabel}
                              </Typography>
                              {available > total * 0.5 && total > 3 && (
                                <Typography variant="caption" color="warning.main" sx={{ display: { xs: 'none', sm: 'block' } }}>
                                  {available} licenses potentially unnecessary
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {total.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {consumed.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {available.toLocaleString()}
                              </Typography>
                              {available > 0 && total > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  ({((available / total) * 100).toFixed(1)}% unused)
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600, 
                                color: pct >= 95 ? 'error.main' : pct >= 85 ? 'warning.main' : 'inherit' 
                              }}
                            >
                              {pct}%
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Chip 
                              label={
                                pct >= 95 ? 'Critical' : 
                                pct >= 80 ? 'High Usage' : 
                                pct >= 50 ? 'Moderate' : 
                                pct < 25 && total > 3 ? 'Underutilized' :
                                pct === 0 ? 'Unused' :
                                'Normal'
                              }
                              size="small" 
                              color={
                                pct >= 95 ? 'error' : 
                                pct >= 80 ? 'warning' : 
                                pct < 25 && total > 3 ? 'info' :
                                pct === 0 ? 'error' :
                                'success'
                              }
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <Box>
                              <Chip 
                                label={risk.level}
                                size="small" 
                                color={risk.color}
                                variant="filled"
                              />
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, display: { xs: 'none', md: 'block' } }}>
                                {risk.reason}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          )}
        </Box>
      )}

      {/* Alerts Dialog */}
      <Dialog 
        open={alertsDialogOpen} 
        onClose={() => setAlertsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {cloudDetail?.provider === 'aws' ? 'AWS Alerts & Recommendations' : 'License Alerts & Recommendations'}
            </Typography>
            <IconButton onClick={() => setAlertsDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {!cloudDetail ? (
            <Box textAlign="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (cloudDetail.provider === 'aws' ? (summary.alerts?.length || 0) : enhancedAlerts.length) === 0 ? (
            <Box textAlign="center" py={4}>
              <AlertIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" color="success.main">All Good!</Typography>
              <Typography variant="body2" color="text.secondary">
                No alerts or recommendations at this time.
              </Typography>
            </Box>
          ) : (
            <List>
              {(cloudDetail.provider === 'aws' ? (summary.alerts || []) : enhancedAlerts).filter(alert => alert && (alert.title || alert.type) && (alert.message || alert.msg)).map((alert, index) => {
                const alertType = alert.type || 'info';
                const alertTitle = alert.title || alert.type || 'Alert';
                const alertMessage = alert.message || alert.msg || '';
                const isError = alertType.toLowerCase().includes('critical') || alertType.toLowerCase().includes('security') || alertType.toLowerCase().includes('waste');
                const isWarning = alertType.toLowerCase().includes('cost') || alertType.toLowerCase().includes('optimization') || alertType.toLowerCase().includes('warning');
                
                return (
                  <ListItem key={index} divider>
                    <ListItemIcon>
                      {isError || alert.severity === 'error' ? (
                        <ErrorIcon color="error" />
                      ) : isWarning || alert.severity === 'warning' ? (
                        <AlertIcon color="warning" />
                      ) : alert.severity === 'success' ? (
                        <AlertIcon color="success" />
                      ) : (
                        <AlertIcon color="info" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {alertTitle}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {alertMessage}
                          </Typography>
                          {alert.action && (
                            <Typography variant="caption" color="primary.main" sx={{ mt: 1, display: 'block', fontWeight: 500 }}>
                              Recommendation: {alert.action}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertsDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => !actionLoading && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon color="error" />
          <Typography variant="h6" fontWeight={600}>
            Remove Cloud Connection
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to remove this cloud connection?
            </Typography>
            {cloudToDelete && (
              <Paper sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Provider:</strong> {cloudToDelete.provider?.toUpperCase() === 'M365' ? 'Microsoft 365' : 'Microsoft Azure'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  <strong>Tenant ID:</strong> {cloudToDelete.tenantId}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  <strong>Status:</strong> {cloudToDelete.status}
                </Typography>
              </Paper>
            )}
            <Typography variant="body2" color="error.main" sx={{ mt: 2, fontWeight: 500 }}>
              ⚠️ This action cannot be undone. All monitoring data for this connection will be removed.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={actionLoading}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete} 
            disabled={actionLoading}
            variant="contained"
            color="error"
            startIcon={actionLoading ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
            sx={{ minWidth: 120 }}
          >
            {actionLoading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <CloudConnectDialog 
        open={dlgOpen} 
        defaultProvider={provider}
        onClose={() => setDlgOpen(false)} 
        onSuccess={async () => { 
          setDlgOpen(false); 
          await loadList(); 
        }} 
      />
    </Container>
  );
}
